import { useRef } from "react";
import { MODULES } from "../constants/modules";
import { PRICING } from "../constants/pricing";
import { COLORS, FONTS } from "../constants/theme";
import Logo from "../components/Logo";
import Counter from "../components/Counter";
import ModulePill from "../components/ModulePill";
import PricingCard from "../components/PricingCard";
import { useInView } from "../hooks/useInView";

// ─── MARKETING SITE ───────────────────────────────────────────────────────────
// The public-facing marketing page at revcentriq.com
// Sections: Nav → Hero → Stats → Platform → Customers → Pricing → CTA → Footer

const STATS = [
  { value: "430+", label: "GTM teams" },
  { value: "94%", label: "forecast accuracy" },
  { value: "3.4x", label: "avg pipeline coverage" },
  { value: "12", label: "modules in one platform" },
];

const TESTIMONIALS = [
  {
    name: "Sarah Chen",
    role: "CRO, Meridian SaaS",
    quote: "RevCentriQ replaced six tools. Our RevOps team went from 3 hours of reporting to 10 minutes.",
    avatar: "SC",
  },
  {
    name: "Marcus Webb",
    role: "VP Sales, Stackline",
    quote: "ForecastIQ alone paid for itself in the first quarter. Our accuracy went from 71% to 94%.",
    avatar: "MW",
  },
  {
    name: "Priya Anand",
    role: "Head of RevOps, Fluxion",
    quote: "OpsIQ surfaced data quality issues we had no idea existed. Game changer for our CRM hygiene.",
    avatar: "PA",
  },
];

export default function MarketingSite({ onLogin, onSignup }) {
  const statsRef = useRef(null);
  const statsVisible = useInView(statsRef);

  return (
    <div style={{ background: COLORS.bgBase, color: COLORS.textPrimary, minHeight: "100vh", fontFamily: FONTS.body }}>

      {/* ── NAV ── */}
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 50, borderBottom: `1px solid ${COLORS.borderLight}`, backdropFilter: "blur(16px)", background: COLORS.bgBase + "cc", padding: "0 40px", height: 62, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Logo size="md" />
        <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
          {["Platform", "Pricing", "Customers"].map((l) => (
            <button key={l} className="nav-link" onClick={() => document.getElementById(l.toLowerCase())?.scrollIntoView({ behavior: "smooth" })}>
              {l}
            </button>
          ))}
          <button className="nav-link" onClick={onLogin}>Sign in</button>
          <button className="btn-primary" style={{ fontSize: 13, padding: "9px 20px" }} onClick={onSignup}>
            Get started free
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "120px 40px 80px", position: "relative", overflow: "hidden" }}>
        {/* Background grid */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(#1a2035 1px,transparent 1px),linear-gradient(90deg,#1a2035 1px,transparent 1px)", backgroundSize: "60px 60px", opacity: .25 }} />
        {/* Radial glow */}
        <div style={{ position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)", width: 800, height: 500, background: "radial-gradient(ellipse,#1d4ed822 0%,transparent 70%)", pointerEvents: "none" }} />

        <div style={{ position: "relative", maxWidth: 860, margin: "0 auto" }}>
          {/* Badge */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#0d1526", border: "1px solid #1e3a6e", borderRadius: 20, padding: "6px 16px", marginBottom: 28, fontSize: 12, fontFamily: FONTS.mono, color: "#60a5fa" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.iqBlueMid, display: "inline-block", animation: "pulse-ring 1.5s ease-out infinite" }} />
            12 modules · One GTM intelligence platform
          </div>

          <h1 style={{ fontFamily: FONTS.display, fontWeight: 800, fontSize: "clamp(42px,6vw,80px)", lineHeight: 1.05, letterSpacing: "-.04em", marginBottom: 24 }}>
            Every revenue metric.<br />
            <span style={{ background: "linear-gradient(135deg,#3b82f6,#8b5cf6,#ec4899)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              One platform.
            </span>
          </h1>

          <p style={{ fontSize: "clamp(16px,2vw,20px)", color: COLORS.textSecondary, lineHeight: 1.7, maxWidth: 600, margin: "0 auto 40px", fontWeight: 400 }}>
            RevCentriQ gives your CRO, CFO, and revenue team a single source of truth — from pipeline to payback period, churn to competitive win rates.
          </p>

          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn-primary" style={{ fontSize: 15, padding: "14px 32px" }} onClick={onSignup}>Start free trial</button>
            <button className="btn-ghost" style={{ fontSize: 15, padding: "13px 28px" }} onClick={onLogin}>View demo →</button>
          </div>
          <p style={{ marginTop: 20, fontSize: 12, color: COLORS.textMuted, fontFamily: FONTS.mono }}>
            No credit card required · 14-day free trial · Setup in under 5 minutes
          </p>
        </div>

        {/* Floating metric badges */}
        <div style={{ position: "absolute", left: "5%", top: "35%", animation: "float 4s ease-in-out infinite" }}>
          <div style={{ background: "#0d1526", border: "1px solid #2563eb44", borderRadius: 10, padding: "10px 14px", fontSize: 12, fontFamily: FONTS.mono, color: "#60a5fa" }}>RevenueIQ ↗ +18.4%</div>
        </div>
        <div style={{ position: "absolute", right: "6%", top: "30%", animation: "float 5s ease-in-out infinite .8s" }}>
          <div style={{ background: "#0d1526", border: "1px solid #059669aa", borderRadius: 10, padding: "10px 14px", fontSize: 12, fontFamily: FONTS.mono, color: "#34d399" }}>NRR · 115% ✓</div>
        </div>
        <div style={{ position: "absolute", left: "8%", bottom: "20%", animation: "float 3.5s ease-in-out infinite .4s" }}>
          <div style={{ background: "#0d1526", border: "1px solid #d9770644", borderRadius: 10, padding: "10px 14px", fontSize: 12, fontFamily: FONTS.mono, color: "#fbbf24" }}>CAC $6,400 ↓22%</div>
        </div>
        <div style={{ position: "absolute", right: "5%", bottom: "22%", animation: "float 4.5s ease-in-out infinite 1.2s" }}>
          <div style={{ background: "#0d1526", border: "1px solid #e11d4844", borderRadius: 10, padding: "10px 14px", fontSize: 12, fontFamily: FONTS.mono, color: "#fb7185" }}>Churn risk · 24 accts</div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section ref={statsRef} style={{ borderTop: `1px solid ${COLORS.borderLight}`, borderBottom: `1px solid ${COLORS.borderLight}`, padding: "48px 40px", background: COLORS.bgSection }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 32, textAlign: "center" }}>
          {STATS.map((s, i) => (
            <div key={i}>
              <p style={{ fontFamily: FONTS.display, fontWeight: 800, fontSize: 36, letterSpacing: "-.04em", color: COLORS.textPrimary }}>
                {statsVisible ? <Counter value={s.value} /> : "0"}
              </p>
              <p style={{ color: COLORS.textSecondary, fontSize: 13, marginTop: 4, fontFamily: FONTS.mono }}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── PLATFORM ── */}
      <section id="platform" style={{ padding: "100px 40px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <p style={{ fontFamily: FONTS.mono, fontSize: 12, color: COLORS.iqBlueMid, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 14 }}>The Platform</p>
          <h2 style={{ fontFamily: FONTS.display, fontWeight: 800, fontSize: "clamp(30px,4vw,52px)", letterSpacing: "-.03em", lineHeight: 1.1, marginBottom: 16 }}>
            12 modules. One subscription.
          </h2>
          <p style={{ color: COLORS.textSecondary, fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.7 }}>
            Each module is a best-in-class analytics tool. Together they're a complete GTM intelligence suite.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
          {MODULES.map((m) => <ModulePill key={m.id} module={m} />)}
        </div>
      </section>

      {/* ── CUSTOMERS ── */}
      <section id="customers" style={{ padding: "80px 40px", background: COLORS.bgSection, borderTop: `1px solid ${COLORS.borderLight}`, borderBottom: `1px solid ${COLORS.borderLight}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <p style={{ fontFamily: FONTS.mono, fontSize: 12, color: COLORS.iqBlueMid, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 14 }}>Customers</p>
            <h2 style={{ fontFamily: FONTS.display, fontWeight: 800, fontSize: "clamp(28px,3.5vw,44px)", letterSpacing: "-.03em" }}>
              Teams that ship with RevCentriQ
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={{ background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: "28px 28px 24px" }}>
                <p style={{ fontSize: 14, lineHeight: 1.75, color: "#a0aec0", marginBottom: 24, fontStyle: "italic" }}>"{t.quote}"</p>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg,#1d4ed8,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff" }}>{t.avatar}</div>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 13, color: COLORS.textPrimary }}>{t.name}</p>
                    <p style={{ fontSize: 11, color: COLORS.textSecondary, fontFamily: FONTS.mono }}>{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" style={{ padding: "100px 40px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <p style={{ fontFamily: FONTS.mono, fontSize: 12, color: COLORS.iqBlueMid, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 14 }}>Pricing</p>
          <h2 style={{ fontFamily: FONTS.display, fontWeight: 800, fontSize: "clamp(28px,3.5vw,48px)", letterSpacing: "-.03em", marginBottom: 14 }}>
            Simple, transparent pricing
          </h2>
          <p style={{ color: COLORS.textSecondary, fontSize: 15 }}>Start with what you need. Scale as your team grows.</p>
        </div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", justifyContent: "center" }}>
          {PRICING.map((p, i) => <PricingCard key={i} tier={p} onCta={onSignup} />)}
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: "80px 40px", textAlign: "center", borderTop: `1px solid ${COLORS.borderLight}`, background: COLORS.bgSection }}>
        <div style={{ marginBottom: 28 }}>
          <Logo size="lg" style={{ justifyContent: "center" }} />
        </div>
        <h2 style={{ fontFamily: FONTS.display, fontWeight: 800, fontSize: "clamp(28px,4vw,52px)", letterSpacing: "-.03em", marginBottom: 16, lineHeight: 1.1 }}>
          Ready to unify your GTM?
        </h2>
        <p style={{ color: COLORS.textSecondary, fontSize: 16, marginBottom: 36, maxWidth: 480, margin: "0 auto 36px" }}>
          Join 430+ revenue teams that replaced their reporting stack with RevCentriQ.
        </p>
        <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
          <button className="btn-primary" style={{ fontSize: 15, padding: "14px 36px" }} onClick={onSignup}>Get started free</button>
          <button className="btn-ghost" style={{ fontSize: 15 }} onClick={onLogin}>Sign in →</button>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ borderTop: `1px solid ${COLORS.borderLight}`, padding: "32px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
        <Logo size="sm" />
        <p style={{ color: COLORS.textMuted, fontSize: 12, fontFamily: FONTS.mono }}>© 2026 RevCentriQ · All rights reserved</p>
        <div style={{ display: "flex", gap: 24 }}>
          {["Privacy", "Terms", "Security"].map((l) => (
            <span key={l} style={{ color: COLORS.textMuted, fontSize: 12, cursor: "pointer" }}>{l}</span>
          ))}
        </div>
      </footer>
    </div>
  );
}
